import React, {
    createContext, useContext, useState, useCallback, useEffect, ReactNode, useMemo,
} from 'react';
import { Platform } from 'react-native';
import {
    HeartsBalance,
    HeartGift,
    ReferralStats,
    MasterWallet,
    MasterHeartRecord,
    PayoutRecord,
    FundTransparency,
    MasterListItem,
    HEARTS_PER_REGISTRATION,
    HEARTS_TO_DOLLAR_RATE,
    MIN_WITHDRAWAL_AMOUNT,
} from '../types/fund';
import { api, getFileUrl } from '../services/api';
import { storage } from '../utils/storage';
import type { PublicMasterProfile } from '../types/auth';

// ── Helpers ────────────────────────────────────────────────

const genId = () => `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

// Master list: on native the provider fetches it (effect below); on web the
// «Фонд» screen loads it on demand (CORE-1386) and this stays empty.

// ── Context interface ──────────────────────────────────────

interface FundContextType {
    // Referral
    inviteLink: string;
    referralStats: ReferralStats;

    // Hearts balance
    heartsBalance: HeartsBalance;
    gifts: HeartGift[];

    // Masters
    masters: MasterListItem[];
    isMasterRole: boolean;

    // Actions
    giveHearts: (masterId: string, masterName: string, masterAvatar: string | undefined, category: string, amount: number) => boolean;

    // Master wallet
    wallet: MasterWallet;
    heartsFromUsers: MasterHeartRecord[];
    payoutHistory: PayoutRecord[];
    convertHearts: (hearts: number) => boolean;
    requestWithdrawal: (amount: number) => boolean;

    // Transparency
    transparency: FundTransparency;

    // Onboarding.
    // CORE-964 — признак читается из хранилища и переживает и пересборку дерева,
    // и перезагрузку. Пока хранилище не ответило, он `true`: показать окно и
    // тут же спрятать хуже, чем показать на полсекунды позже.
    hasSeenOnboarding: boolean;
    markOnboardingSeen: () => void;
}

const FundContext = createContext<FundContextType | null>(null);

// ── Provider ───────────────────────────────────────────────

// CORE-1386 — та же свёртка, что в нативной ветке провайдера ниже; веб зовёт её
// из FundScreen. Схлопнуть в одну, когда лишний запрос уберут и с телефона.
export function toMasterListItem(m: PublicMasterProfile): MasterListItem {
    return {
        id: String(m.id),
        name: m.name,
        avatar: getFileUrl(m.photoUrl) || '',
        category: (m.description || '').split(/[•·,.]/, 1)[0].trim() || 'Master',
    };
}

export const FundProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    // In production: isMasterRole comes from AuthContext
    const [isMasterRole] = useState(true);

    // ── Masters list (live from /api/MasterProfile/public) ──
    const [masters, setMasters] = useState<MasterListItem[]>([]);
    useEffect(() => {
        // CORE-1386 — на вебе список здесь не грузим: его единственный читатель —
        // экран «Фонд», и на проде тот показывает «Coming Soon», а запрос уходил
        // при каждом старте приложения, в том числе с главной. Веб берёт список
        // сам, когда экран открыт (FundScreen). Ниже — нативная ветка как была.
        if (Platform.OS === 'web') return;
        let cancelled = false;
        (async () => {
            try {
                const list = await api.getPublicMasterProfiles();
                if (cancelled) return;
                setMasters(list.map(m => ({
                    id: String(m.id),
                    name: m.name,
                    avatar: getFileUrl(m.photoUrl) || '',
                    category: (m.description || '').split(/[•·,.]/, 1)[0].trim() || 'Master',
                })));
            } catch { /* keep empty list — Hearts UI handles it */ }
        })();
        return () => { cancelled = true; };
    }, []);

    // ── Referral data ──────────────────────────────────────
    const inviteLink = 'https://agape.com/invite/abc123';

    const [referralStats] = useState<ReferralStats>({
        registrations: 25,
        subscriptions: 4,
        heartsEarned: 25 * HEARTS_PER_REGISTRATION, // 250
    });

    // ── Hearts balance ─────────────────────────────────────
    const [heartsBalance, setHeartsBalance] = useState<HeartsBalance>({
        totalEarned: 250,
        pending: 30, // 3 recent registrations
        totalSent: 100, // already gifted
        available: 120, // 250 - 30 - 100
    });

    const [gifts, setGifts] = useState<HeartGift[]>([]);

    // ── Master wallet ──────────────────────────────────────
    const [wallet, setWallet] = useState<MasterWallet>({
        totalHeartsReceived: 1420,
        heartsConverted: 1000,
        heartsToConvert: 420,
        availableBalance: 10.0,       // $10 from 1000 converted
        pendingBalance: 0,
        totalWithdrawn: 0,
        hasPayoutMethod: true,
    });

    const [heartsFromUsers] = useState<MasterHeartRecord[]>([
        { fromUserId: 'u1', fromUserName: 'Elena K.', heartsAmount: 50, date: '2026-03-02' },
        { fromUserId: 'u2', fromUserName: 'Dmitry S.', heartsAmount: 30, date: '2026-03-01' },
        { fromUserId: 'u3', fromUserName: 'Natasha P.', heartsAmount: 120, date: '2026-02-28' },
        { fromUserId: 'u4', fromUserName: 'Alex M.', heartsAmount: 80, date: '2026-02-25' },
        { fromUserId: 'u5', fromUserName: 'Maria V.', heartsAmount: 140, date: '2026-02-20' },
    ]);

    const [payoutHistory, setPayoutHistory] = useState<PayoutRecord[]>([]);

    // ── Onboarding ─────────────────────────────────────────
    // CORE-964 — раньше признак был `useState(false)` и жил только в памяти:
    // окно возвращалось на каждой перезагрузке и при любом пересоздании
    // провайдера. Пользователю это видно как «нажал, а окно опять открыто», то
    // есть как неработающая кнопка.
    const [hasSeenOnboarding, setHasSeenOnboarding] = useState(true);
    useEffect(() => {
        let cancelled = false;
        storage.getFundOnboardingSeen()
            .then(seen => { if (!cancelled) setHasSeenOnboarding(seen); })
            .catch(() => { /* хранилище недоступно — окно просто не показываем */ });
        return () => { cancelled = true; };
    }, []);

    // ── Transparency ───────────────────────────────────────
    const transparency = useMemo((): FundTransparency => ({
        totalUsersOnPlatform: 2400,
        totalMasters: 48,
        totalHeartsDistributed: 48000,
        totalHeartsInCirculation: 120000,
        totalPaidOutToMasters: 480,
        currentRatePerHeart: 1 / HEARTS_TO_DOLLAR_RATE,
    }), []);

    // ── Give hearts (user → master) ────────────────────────

    const giveHearts = useCallback((
        masterId: string, masterName: string, masterAvatar: string | undefined,
        category: string, amount: number,
    ): boolean => {
        if (amount <= 0 || amount > heartsBalance.available) return false;
        setGifts(prev => [{
            id: genId(), fromUserId: 'user_1',
            toMasterId: masterId, toMasterName: masterName, toMasterAvatar: masterAvatar,
            toMasterCategory: category, heartsAmount: amount,
            createdAt: new Date().toISOString(),
        }, ...prev]);
        setHeartsBalance(prev => ({
            ...prev,
            totalSent: prev.totalSent + amount,
            available: prev.available - amount,
        }));
        return true;
    }, [heartsBalance]);

    // ── Convert hearts → money (master) ────────────────────

    const convertHearts = useCallback((hearts: number): boolean => {
        if (hearts <= 0 || hearts > wallet.heartsToConvert) return false;
        const dollars = hearts / HEARTS_TO_DOLLAR_RATE;
        setWallet(prev => ({
            ...prev,
            heartsConverted: prev.heartsConverted + hearts,
            heartsToConvert: prev.heartsToConvert - hearts,
            availableBalance: prev.availableBalance + dollars,
        }));
        return true;
    }, [wallet]);

    // ── Withdraw (master) ──────────────────────────────────

    const requestWithdrawal = useCallback((amount: number): boolean => {
        if (amount < MIN_WITHDRAWAL_AMOUNT || amount > wallet.availableBalance) return false;
        const rec: PayoutRecord = {
            id: genId(), date: new Date().toISOString(),
            amount, heartsConverted: Math.round(amount * HEARTS_TO_DOLLAR_RATE),
            status: 'processing',
        };
        setPayoutHistory(prev => [rec, ...prev]);
        setWallet(prev => ({
            ...prev,
            availableBalance: prev.availableBalance - amount,
            pendingBalance: prev.pendingBalance + amount,
        }));
        return true;
    }, [wallet]);

    const markOnboardingSeen = useCallback(() => {
        setHasSeenOnboarding(true);
        void storage.setFundOnboardingSeen();
    }, []);

    return (
        <FundContext.Provider value={{
            inviteLink, referralStats,
            heartsBalance, gifts, masters, isMasterRole,
            giveHearts,
            wallet, heartsFromUsers, payoutHistory,
            convertHearts, requestWithdrawal,
            transparency,
            hasSeenOnboarding, markOnboardingSeen,
        }}>
            {children}
        </FundContext.Provider>
    );
};

export const useFund = (): FundContextType => {
    const ctx = useContext(FundContext);
    if (!ctx) throw new Error('useFund must be used within a FundProvider');
    return ctx;
};
